import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/supplier-operations';

const SupplierOperationsLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Supplier Operations Hub | Explanatum';
    const desc = 'Source vetted suppliers, manage CRM pipelines, and automate vendor relationships—without spreadsheet silos or procurement retainers.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Supplier operations overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-blue-100/10 px-4 py-1 text-sm font-semibold text-cyan-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-cyan-300" />
                AI Supplier Operations Hub
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Find trade suppliers and manage them like top-tier procurement.
                <span className="mt-2 block text-blue-600">
                  Sourcing, CRM, compliance, and relationship health in one view.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Run supplier discovery, track onboarded vendors, and monitor quotes inside a shared workspace. Explanatum replaces spreadsheet trackers and procurement consultants with AI workflows that keep growth teams and operations aligned.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_supplier_ops_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-cyan-500 via-indigo-500 to-violet-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/supplier-crm"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open supplier workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  AI Supplier Finder with location, category, and licensing context
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Supplier CRM with status, notes, documents, and contact drawers
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-violet-300" />
                  Quotes, follow-ups, and compliance metrics automated end-to-end
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Supplier dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-100/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Supplier pipeline</span>
                  <span>Live insights</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">New sourcing brief → 12 vetted suppliers across EU & APAC • Filters by MOQ, lead time, certification.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Supplier CRM snapshot: 48 active partners • 7 on onboarding • Burn-down chart highlights compliance gaps.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">AI Consultant drafting quarterly vendor review with margin impact, SLA risk, and negotiation playbook.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Supplier sourcing and CRM, finally connected</h2>
            <p className="mt-4 text-base text-gray-500">Stop rebuilding vendor shortlists every quarter. Keep discovery, negotiation, and compliance aligned for the whole team.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'AI sourcing briefs',
              body: 'Describe what you need and instantly receive structured supplier lists with licensing, region, and contact details.'
            }, {
              title: 'Supplier CRM workspace',
              body: 'Track statuses, tasks, and relationship notes with automated metrics across organisations and categories.'
            }, {
              title: 'Quote and contract tracking',
              body: 'Log quotes, documents, and renewals per supplier so pricing negotiations and compliance are never siloed.'
            }, {
              title: 'Collaboration & alerts',
              body: 'Invite operations, marketing, and finance to the same view with Slack/WhatsApp alerts when milestones hit.'
            }, {
              title: 'Governance baked in',
              body: 'Tag certifications, geography constraints, and risk levels so procurement decisions stay audit-ready.'
            }, {
              title: 'Consultant-level summaries',
              body: 'Let the AI Business Consultant draft executive updates, RFP recaps, and negotiation scripts in minutes.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams leave spreadsheets and procurement consultants</h2>
            <p className="mt-4 text-base text-gray-500">Centralise supplier knowledge, move fast on sourcing requests, and keep commercial leverage without paying agency retainers.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Supplier Operations Hub',
              points: [
                'AI sourcing briefs plus CRM in one workspace',
                'Contact drawers, quotes, compliance logs, and alerts',
                'Shared context for ops, finance, and GTM teams',
                'Consultant-style reporting without extra headcount',
                'Predictable subscription—no per-project fees'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Manual spreadsheets & share drives',
              points: [
                'Scattered supplier tabs with outdated contacts',
                'No unified history of quotes, renewals, or SLA breaches',
                'Difficult to filter by compliance or certification needs',
                'Slow onboarding—tribal knowledge stuck in emails',
                'Time lost preparing executive-ready sourcing updates'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Procurement consultants & sourcing agencies',
              points: [
                '£4,000+ retainers per project with limited transparency',
                'Insights delivered as static decks, not living systems',
                'Vendor knowledge disappears after engagements end',
                'Slow to react when requirements or markets change',
                'Extra fees for each negotiation or compliance review'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-blue-200 bg-blue-600/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need three compliant suppliers before month-end?”</h3>
            <p className="mt-3 text-base text-gray-600">Launch a sourcing brief, shortlist candidates, and share CRM-ready onboarding notes today—no consultant kickoff required.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_supplier_ops_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-cyan-500 via-indigo-500 to-violet-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-cyan-400/30 hover:scale-[1.02]"
              >
                Launch supplier ops now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with AI Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where supplier operations automation pays off</h2>
            <p className="mt-4 text-base text-gray-500">Give every sourcing project a repeatable playbook, from product launches to global expansion.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Consumer product launches',
              body: 'Source white-label and licensed suppliers while tracking lead times, certifications, and unit economics.'
            }, {
              title: 'Hospitality & retail rollouts',
              body: 'Maintain approved vendor lists, compare quotes, and ensure compliance across locations.'
            }, {
              title: 'Agency & procurement teams',
              body: 'Build partner directories, automate follow-ups, and share negotiation intel with clients instantly.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default SupplierOperationsLanding;
